import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import CircleIcon from "@mui/icons-material/Circle";
// MUI's name for the glyph is "WarningAmber"; the amber is in the icon set's
// name, not in ours — we tint it with BRAND_COLOR below.
import WarningIcon from "@mui/icons-material/WarningAmber";
import {
  BRAND_COLOR_TINT,
  CARD_BORDER,
  ERROR_BORDER,
  ERROR_COLOR,
  SURFACE_TRANSPARENT,
} from "../utils/colors";
import {
  FS_BODY,
  FS_CAPTION,
  FS_MICRO,
  FS_TITLE,
  FW_SEMIBOLD,
} from "../utils/typography";

const DATA_CENTER_NAMES = {
  "https://www.zohoapis.com": "US",
  "https://www.zohoapis.eu": "EU",
  "https://www.zohoapis.com.au": "AU",
  "https://www.zohoapis.in": "IN",
  "https://www.zohoapis.com.cn": "CN",
  "https://www.zohoapis.jp": "JP",
};

const DetailTile = ({ label, value }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 180,
      p: 1.75,
      borderRadius: "8px",
      // outlined rather than filled — the three tiles would run together
      // without something separating them
      border: CARD_BORDER,
    }}
  >
    <Typography
      sx={{
        fontSize: FS_MICRO,
        fontWeight: FW_SEMIBOLD,
        color: "text.secondary",
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: FS_BODY, fontWeight: FW_SEMIBOLD, wordBreak: "break-all" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

// Authentication tab: connection summary card + details + revoke banner.
export default function Overview({
  handleRevokeZoho,
  revokeLoadingZoho,
  authLoadingZoho,
  zohoAuth,
  handleAuthenticateZoho,
  orgId,
  dataCenterUrl,
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Connection summary */}
      <Card sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "8px",
            bgcolor: BRAND_COLOR_TINT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <VpnKeyIcon color="primary" sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: FW_SEMIBOLD, fontSize: FS_TITLE }}>
              Zoho CRM
            </Typography>
            <Chip
              size="small"
              label={zohoAuth ? "Connected" : "Not Connected"}
              color={zohoAuth ? "success" : "default"}
              variant={zohoAuth ? "filled" : "outlined"}
              sx={{ fontWeight: FW_SEMIBOLD, fontSize: FS_MICRO }}
            />
          </Box>
          <Typography sx={{ fontSize: FS_BODY, color: "text.secondary" }}>
            Authorize Easy Postgres to read your CRM modules and keep them in
            sync with your PostgreSQL database.
          </Typography>
        </Box>
        {zohoAuth ? (
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography
              sx={{
                fontSize: FS_MICRO,
                fontWeight: FW_SEMIBOLD,
                letterSpacing: 0.6,
                color: "text.secondary",
              }}
            >
              CONNECTION STATUS
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                justifyContent: "flex-end",
              }}
            >
              <CircleIcon sx={{ fontSize: 10, color: "success.main" }} />
              <Typography sx={{ fontWeight: FW_SEMIBOLD, fontSize: FS_BODY }}>
                Healthy
              </Typography>
            </Box>
          </Box>
        ) : (
          <LoadingButton
            variant="contained"
            loading={authLoadingZoho}
            loadingIndicator="Waiting…"
            onClick={() => {
              handleAuthenticateZoho();
            }}
            sx={{ minWidth: 150, whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Authenticate
          </LoadingButton>
        )}
      </Card>

      {zohoAuth && (
        <>
          {/* Connection details */}
          <Card sx={{ p: 2.5 }}>
            <Typography
              sx={{ fontWeight: FW_SEMIBOLD, fontSize: FS_TITLE, mb: 1.75 }}
            >
              Connection Details
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <DetailTile
                label="DATA CENTER"
                value={DATA_CENTER_NAMES[dataCenterUrl] || dataCenterUrl}
              />
              <DetailTile label="ORGANIZATION ID" value={orgId} />
              <DetailTile label="API URL" value={dataCenterUrl} />
            </Box>
          </Card>

          {/* Revoke banner */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              borderRadius: "10px",
              // spec §5: transparent danger-zone card, defined by its red
              // outline and the red outlined button inside it
              bgcolor: SURFACE_TRANSPARENT,
              border: `1px solid ${ERROR_BORDER}`,
            }}
          >
            <WarningIcon sx={{ color: ERROR_COLOR }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: FW_SEMIBOLD, fontSize: FS_BODY }}>
                Revoke Access
              </Typography>
              <Typography sx={{ fontSize: FS_CAPTION, color: "text.secondary" }}>
                Disconnecting stops all module syncs until you authenticate
                again.
              </Typography>
            </Box>
            <LoadingButton
              variant="outlined"
              color="error"
              loading={revokeLoadingZoho}
              onClick={handleRevokeZoho}
              sx={{ whiteSpace: "nowrap" }}
            >
              Revoke Access
            </LoadingButton>
          </Box>
        </>
      )}
    </Box>
  );
}
